const redacted = '[redacted]';

// Matched as text rather than parsed as URLs because the SDKs also embed them in span names and
// breadcrumb messages. The login return URL nests the consent URL in its `redirect` query, where
// Vue Router leaves the `=` before the pending ID raw but other encoders escape it.
const pendingIdInQuery = /(authorization_request_id(?:=|%3D))[^&#\s"']+/gi;
const pendingIdInReadPath = /(\/oauth\/authorize\/requests\/)[^/?#\s"']+/gi;

// Consent reads, approvals and denials carry the state and the callback, code included, in their
// bodies, and partner app creation and secret rotation carry the client secret, so recorded
// requests to them are dropped rather than redacted.
const platformOauthApiUrl = /^(?:https?:\/\/[^/]+)?\/(?:oauth\/authorize|api\/users\/partner-apps)(?:[/?#]|$)/i;

export const redactPlatformOauthSecrets = (text: string): string => text
    .replace(pendingIdInQuery, `$1${redacted}`)
    .replace(pendingIdInReadPath, `$1${redacted}`);

export const containsPlatformOauthSecret = (text: string): boolean => redactPlatformOauthSecrets(text) !== text;

export const isPlatformOauthApiUrl = (url: string): boolean => platformOauthApiUrl.test(url);

export const redactPlatformOauthValues = <T extends Record<string, unknown>>(values: T): T => {
    const redactedValues = { ...values };

    for (const [key, value] of Object.entries(values)) {
        if (typeof value === 'string') {
            Object.assign(redactedValues, { [key]: redactPlatformOauthSecrets(value) });
        }
    }

    return redactedValues;
};

// Serialising also reaches values the SDK nests (web vitals, $set_once) or uses as keys (heatmaps).
// Untouched values are returned as-is so unrelated payloads never round-trip through JSON.
export const redactPlatformOauthNested = <T>(value: T): T => {
    if (value === undefined) {
        return value;
    }

    const serialized = JSON.stringify(value);
    if (!containsPlatformOauthSecret(serialized)) {
        return value;
    }

    return JSON.parse(redactPlatformOauthSecrets(serialized));
};
